import { type ReactNode, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { cn } from '@/lib/utils'

interface ActivityAnchorProps {
  activityId: string
  children: ReactNode
}

// Wraps each activity so a `#activity-<id>` deep link can scroll to it and
// briefly flash a yellow ring (visible over any inner card type).
export function ActivityAnchor({ activityId, children }: ActivityAnchorProps) {
  const location = useLocation()
  const ref = useRef<HTMLDivElement>(null)
  const [flashing, setFlashing] = useState(false)

  useEffect(() => {
    if (location.hash !== `#activity-${activityId}`) return
    ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    setFlashing(true)
    const timer = setTimeout(() => setFlashing(false), 1200)
    return () => clearTimeout(timer)
  }, [location.hash, activityId])

  return (
    <div
      ref={ref}
      id={`activity-${activityId}`}
      className={cn(
        'scroll-mt-24 rounded-lg transition-shadow duration-500',
        flashing && 'ring-2 ring-yellow-300'
      )}
    >
      {children}
    </div>
  )
}
